"use client";

import React from "react";
import { Mail, MapPin, Phone, MessageCircle, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactDetails = [{
  icon: MapPin,
  title: "Office Location",
  detail: "1441 Woodmont NW, Atlanta, GA"
}, {
  icon: Phone,
  title: "Phone Number",
  detail: "+1 (404) 555-0123"
}, {
  icon: Mail,
  title: "Email Address",
  detail: "team@quitmytimeshare.com"
}];
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="187782" className="p-8 bg-card/60 backdrop-blur-xl border border-primary/30 rounded-lg text-center animate-in fade-in duration-500 shadow-xl">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-6">
          <Mail className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default text-primary mb-4">Request Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="bg-background/50 border-primary/20 text-foreground placeholder:text-muted-foreground focus-visible:ring-secondary rounded-md h-12" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background/50 border-primary/20 text-foreground placeholder:text-muted-foreground focus-visible:ring-secondary rounded-md h-12" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(404) 555-0123" className="bg-background/50 border-primary/20 text-foreground placeholder:text-muted-foreground focus-visible:ring-secondary rounded-md h-12" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Please provide some details about your situation"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium">Tell us about your timeshare (Resort, Mortgage, etc.)</FormLabel>
              <FormControl>
                <Textarea placeholder="How can we assist you with your exit today?" className="bg-background/50 border-primary/20 text-foreground placeholder:text-muted-foreground focus-visible:ring-secondary rounded-md min-h-[120px] resize-none" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/50 rounded-md">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold text-lg transition-all duration-300 border-none shadow-lg shadow-secondary/20">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 w-5 h-5" />
            Verifying Eligibility...
          </> : "Check My Eligibility"}
      </Button>
    </Form>;
}
export default function Contact() {
  const {
    ref: leftColRef,
    isVisible: leftColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightColRef,
    isVisible: rightColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="eligibilityform" className="bg-background text-foreground">
      <div className="bg-background py-[100px] relative overflow-hidden">
        {}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-secondary/5 blur-[120px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            
            {}
            <div ref={leftColRef} className={`transition-all duration-1000 ease-out ${leftColVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <div className="mb-12">
                <h2 className="font-default text-primary text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  Break Free From the Timeshare Burden
                </h2>
                <p className="text-muted-foreground text-lg md:text-xl font-default leading-relaxed max-w-lg">
                  Help for owners facing fees, mortgages, or misrepresentation. Our expert team provides the trusted guidance you need to navigate the exit process with confidence.
                </p>
              </div>

              <div className="space-y-6 mb-12">
                {contactDetails.map((item, i) => <Card key={i} data-index={i} className="bg-card/40 backdrop-blur-xl border border-primary/10 rounded-lg overflow-hidden hover:bg-card/60 transition-colors duration-300 shadow-sm">
                    <CardContent className="p-6 flex items-center gap-6">
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary/10 border border-primary/20 rounded-md">
                        <item.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-muted-foreground text-sm font-medium mb-1 uppercase tracking-wider">
                          {item.title}
                        </h4>
                        <p className="text-primary text-lg font-medium">
                          {item.detail}
                        </p>
                      </div>
                    </CardContent>
                  </Card>)}
              </div>

              <div>
                <Button asChild className="w-full sm:w-auto h-14 px-8 rounded-md bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold text-lg transition-all duration-300 border-none shadow-lg shadow-secondary/20 group">
                  <Link to="#">
                    <MessageCircle className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
                    Speak with a Specialist
                  </Link>
                </Button>
              </div>
            </div>

            {}
            <div ref={rightColRef} className={`transition-all duration-1000 delay-200 ease-out ${rightColVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <div className="bg-card/80 backdrop-blur-2xl border border-primary/20 rounded-xl p-8 md:p-10 shadow-2xl">
                <h3 className="text-2xl font-default text-primary mb-8 border-b border-primary/10 pb-4">
                  See If You May Qualify
                </h3>
                
                <FormProvider formKey="174" formId="174" sectionName="contact" defaultValues={{
                name: "",
                email: "",
                phone: "",
                message: ""
              }} submitText="Check My Eligibility" successMessage="Thank you for your inquiry. Our specialists will review your details and contact you shortly regarding your eligibility.">
                  <ContactFormContent />
                </FormProvider>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}